// =============================================================================
// Cookie-backed session store.
//
// Implements the Web Storage API surface the app already uses
// (getItem / setItem / removeItem / clear) but backs it with a SESSION COOKIE
// instead of window.sessionStorage. This changes the login lifetime to:
//   • shared across every tab/window of the same browser (cookies are per-origin,
//     so a single sign-in works in all tabs), and
//   • cleared when the browser is fully closed (a session cookie has no
//     Max-Age/Expires), unlike localStorage which would persist indefinitely.
//
// Call sites don't change: each module does
//     import sessionStorage from "...../sessionStore";
// which binds this default export to the name `sessionStorage`, shadowing the
// browser global for that module. Existing `sessionStorage.getItem(...)` calls
// then route through here untouched.
//
// Auth to the API still rides on the `Authorization: Bearer` header the app
// attaches (auth.js) — the token just lives in a cookie now. The cookie is
// scoped to the app's own origin, so it is NOT sent to a cross-origin API
// (REACT_APP_API_URL); there's no new CSRF surface.
//
// CAVEAT: browsers configured to "continue where you left off" / restore the
// previous session (Chrome's default for some setups) can preserve session
// cookies across a restart. The tokenExpiry check in auth.js still enforces a
// hard lifetime regardless, so a stale token is rejected either way.
// =============================================================================

// Namespace so these never collide with any other cookies on the origin.
const PREFIX = "sm_";

function hasDoc() {
  return typeof document !== "undefined";
}

// Parse document.cookie once into a { key: value } map of OUR cookies only.
function readAll() {
  const out = {};
  if (!hasDoc() || !document.cookie) return out;
  document.cookie.split("; ").forEach((pair) => {
    const idx = pair.indexOf("=");
    if (idx === -1) return;
    const rawKey = pair.slice(0, idx);
    if (!rawKey.startsWith(PREFIX)) return;
    const key = decodeURIComponent(rawKey.slice(PREFIX.length));
    out[key] = decodeURIComponent(pair.slice(idx + 1));
  });
  return out;
}

function getItem(key) {
  const all = readAll();
  return Object.prototype.hasOwnProperty.call(all, key) ? all[key] : null;
}

function setItem(key, value) {
  if (!hasDoc()) return;
  const name = PREFIX + encodeURIComponent(key);
  // No Max-Age / Expires => session cookie (dropped on browser close).
  // SameSite=Lax + path=/ so every page in the app can read it; Secure on https.
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${name}=${encodeURIComponent(String(value))}; path=/; SameSite=Lax${secure}`;
}

function removeItem(key) {
  if (!hasDoc()) return;
  const name = PREFIX + encodeURIComponent(key);
  // Expire immediately to delete. Path must match the one used on write.
  document.cookie = `${name}=; path=/; Max-Age=0; SameSite=Lax`;
}

function clear() {
  const all = readAll();
  Object.keys(all).forEach((key) => removeItem(key));
}

const sessionStore = { getItem, setItem, removeItem, clear };

export default sessionStore;
